// Balance-sheet reconciliation display helpers.
//
// 'source_unbalanced' means the mapped section totals tie to the source's
// real Assets / Liabilities / Equity anchors, so any remaining A - (L + E)
// gap is the source workbook's own imbalance, not a mapping error. Newer
// backends stamp the status (and a per-period `ties_to_source` flag) directly;
// sessions persisted before that carry status 'off' with only the per-section
// tie-outs on `section_rollups`, so we also derive it client-side to keep old
// sessions rendering correctly.
//
// Anchor evidence grades (rollup `anchor_source`): 'stated' and 'composite'
// are real source evidence; 'synthetic' means the section was anchored to the
// mapped sum itself (tie_out 0 by construction) and never counts. Ties require
// every real section within tolerance AND at least two real sections. Legacy
// rollups carry no grade: a numeric tie_out is treated as stated.

// Mirrors the backend's BALANCE_TOLERANCE in src/balance_checker/calculator.py.
const TIE_TOLERANCE = 1.0;

const SECTIONS = ['assets', 'liabilities', 'equity'];

export const periodTiesToSource = (pb: any): boolean => {
  if (!pb) return false;
  if (typeof pb.ties_to_source === 'boolean') return pb.ties_to_source;
  const bySection: Record<string, any> = {};
  for (const r of pb.section_rollups || []) {
    if (r && r.section) bySection[r.section] = r;
  }
  let real = 0;
  let allTie = true;
  for (const s of SECTIONS) {
    const r = bySection[s];
    const src = r?.anchor_source;
    const isReal =
      src === 'stated' ||
      src === 'composite' ||
      (src === undefined && typeof r?.tie_out === 'number');
    if (!isReal) continue;
    real += 1;
    if (!(typeof r.tie_out === 'number' && Math.abs(r.tie_out) <= TIE_TOLERANCE)) {
      allTie = false;
    }
  }
  return real >= 2 && allTie;
};

export const effectiveBsStatus = (mapping: any): string => {
  const status = mapping?.reconciliation?.status || 'unknown';
  if (status !== 'off') return status;
  const breakdowns = mapping?.period_breakdowns || [];
  if (!breakdowns.length) return status;
  const allTie = breakdowns.every(
    (pb: any) => pb?.passes !== false || periodTiesToSource(pb),
  );
  return allTie ? 'source_unbalanced' : status;
};
